import { useMemo, useState } from "react";
import { Text } from "@musa.tech/design-system";
import { SegmentedControl } from "@/components/dashboard/SegmentedControl";
import type { GranularityType } from "@/hooks/useEvolutionChartData";
import type { EstablishmentVolumePerDay } from "@/integrations/agreements/client";
import { VolumeHeatmapGrid } from "./VolumeHeatmapGrid";
import { buildHeatmapData } from "./volumeHeatmapAggregation";

const GRANULARITY_OPTIONS = [
  { value: "day", label: "Diário" },
  { value: "week", label: "Semanal" },
  { value: "month", label: "Mensal" },
];

interface VolumeCollectedWidgetProps {
  rows: EstablishmentVolumePerDay[];
}

/**
 * Widget "Volume coletado" (Figma node 233:2567): mapa de calor de unidades ×
 * períodos.
 *
 * A granularidade é estado local e o backend manda o grão diário com as três
 * chaves de período na mesma linha — trocar Diário/Semanal/Mensal reagrupa em
 * memória, sem refetch.
 */
export const VolumeCollectedWidget = ({
  rows,
}: VolumeCollectedWidgetProps) => {
  // Semanal por padrão (Figma node 233:2577): no grão diário um intervalo de
  // poucos meses já passa de 60 colunas e a grade nasce rolando.
  const [granularity, setGranularity] = useState<GranularityType>("week");

  const heatmapData = useMemo(
    () => buildHeatmapData(rows, granularity),
    [rows, granularity],
  );

  return (
    <div className="flex h-full min-w-0 w-full flex-col justify-between gap-6 rounded-2xl bg-white p-6 transition-shadow duration-200 hover:shadow-md motion-reduce:transition-none">
      {/* Título e toggle só dividem a linha a partir de `lg`: no tablet, com a
          sidebar aberta, sobram ~490px e o "Mensal" era cortado. */}
      <div className="flex flex-col gap-4 lg:flex-row lg:items-center lg:justify-between">
        <div className="flex flex-col">
          <Text className="text-xl font-light leading-7 tracking-[1px] text-black">
            Volume coletado
          </Text>
          <Text variant="body" className="text-base text-neutral-700">
            Em litros coletados
          </Text>
        </div>
        <div className="flex flex-wrap items-center gap-4">
          <Text variant="label" size="small" className="whitespace-nowrap text-neutral-600">
            Período:
          </Text>
          <SegmentedControl
            value={granularity}
            onChange={setGranularity}
            options={GRANULARITY_OPTIONS}
            aria-label="Selecionar período de agrupamento do volume coletado"
          />
        </div>
      </div>

      <div className="flex min-w-0 flex-1 flex-col">
        <VolumeHeatmapGrid data={heatmapData} granularity={granularity} />
      </div>
    </div>
  );
};
